import { StationeryDraftFormat, StationeryTemplateCategoryEnum } from '@graphql/generated-base';
import type { CardDimensions, NavigationStep } from '../routes/CardCustomizer/CardCustomizer.types';
import { useCallback, useMemo } from 'react';
import { isPostcard } from '../utils/cardSizeName';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';

interface CardCustomizerConfig {
  steps: NavigationStep[];
}

const CONFIG_BASE: Record<StationeryDraftFormat, CardCustomizerConfig> = {
  [StationeryDraftFormat.digital]: {
    steps: ['cardFront', 'cardBack', 'envelope', 'review', 'postCheckout', 'success'],
  },
  [StationeryDraftFormat.print]: {
    steps: ['cardFront', 'cardBack', 'envelope', 'recipients', 'review', 'postCheckout'],
  },
};

const OVERRIDES: Record<StationeryDraftFormat, Partial<Record<StationeryTemplateCategoryEnum, CardCustomizerConfig>>> = {
  [StationeryDraftFormat.digital]: {},
  [StationeryDraftFormat.print]: {
    [StationeryTemplateCategoryEnum.enclosure]: {
      steps: ['cardFront', 'cardBack', 'review', 'postCheckout'],
    },
    [StationeryTemplateCategoryEnum.menu]: {
      steps: ['cardFront', 'review', 'postCheckout'],
    },
    [StationeryTemplateCategoryEnum.napkin]: {
      steps: ['cardFront', 'review', 'postCheckout'],
    },
    [StationeryTemplateCategoryEnum.sign]: {
      steps: ['cardFront', 'review', 'postCheckout'],
    },
  },
};

// Postcards have no envelopes — the card back is the hand-addressed mailing
// surface — so the envelope and recipient-addressing steps don't apply.
const POSTCARD_EXCLUDED_STEPS: ReadonlyArray<NavigationStep> = ['envelope', 'recipients'];

// Magnets (the MAGNET4X6 product on the 4x6 postcard templates) are
// single-sided — no printable back — so only the back step drops. Unlike the
// postcards they share templates with, magnets mail in A6 envelopes, so the
// envelope and recipients steps stay (card_service renders the envelope at A6
// for the magnet product). The postcardRecipients step never applies: there
// is no back to address.
const MAGNET_EXCLUDED_STEPS: ReadonlyArray<NavigationStep> = ['cardBack'];

// A digital postcard (the matching digital card for a print postcard) has no envelope either: the
// cards show without one, so the step that sets its colour, opening and music drops.
const DIGITAL_POSTCARD_EXCLUDED_STEPS: ReadonlyArray<NavigationStep> = ['envelope'];

// Standalone envelopes: the product IS the envelope, so there is no card to design.
// Stated outright rather than subtracted from CONFIG_BASE, because the base list leads
// with `cardFront` and a filter would preserve that ordering — this flow has to OPEN on
// the envelope step. `postCheckout` is not user-facing; it is the Stripe return landing.
const ENVELOPE_PRODUCT_STEPS: ReadonlyArray<NavigationStep> = ['envelope', 'recipients', 'review', 'postCheckout'];

export const resolveCardCustomizerConfig = (
  format: StationeryDraftFormat,
  category: StationeryTemplateCategoryEnum,
  dimensions?: CardDimensions,
  postcardRecipientsEnabled?: boolean,
  productType?: string,
): CardCustomizerConfig => {
  const config = { ...CONFIG_BASE[format], ...(OVERRIDES[format]?.[category] || {}) };
  // Standalone envelopes are a product, not a category: the draft hangs off a carrier
  // template whose category exists only to satisfy the mandatory template link, so the
  // category and dimension rules below must not get a say. Returned before them.
  if (format === StationeryDraftFormat.print && productType === 'envelope') {
    return { ...config, steps: [...ENVELOPE_PRODUCT_STEPS] };
  }
  // The magnet PRODUCT overrides the category/size rules: it shares the
  // saveTheDate postcard templates but is a different physical product.
  if (format === StationeryDraftFormat.print && productType === 'magnet') {
    return { ...config, steps: config.steps.filter(step => !MAGNET_EXCLUDED_STEPS.includes(step)) };
  }
  const isPostcardSize = !!dimensions && isPostcard(category, dimensions.width, dimensions.height);
  if (format === StationeryDraftFormat.digital && isPostcardSize) {
    return { ...config, steps: config.steps.filter(step => !DIGITAL_POSTCARD_EXCLUDED_STEPS.includes(step)) };
  }
  const isPostcardDraft = format === StationeryDraftFormat.print && isPostcardSize;
  if (!isPostcardDraft) return config;
  // Subtract rather than hardcode a parallel list so future changes to the
  // base print steps reach postcards too.
  const filteredSteps = config.steps.filter(step => !POSTCARD_EXCLUDED_STEPS.includes(step));
  if (!postcardRecipientsEnabled) return { ...config, steps: filteredSteps };
  // Insert postcardRecipients before 'review' when the flag is on.
  const reviewIndex = filteredSteps.indexOf('review');
  const insertAt = reviewIndex === -1 ? filteredSteps.length : reviewIndex;
  return {
    ...config,
    steps: [...filteredSteps.slice(0, insertAt), 'postcardRecipients', ...filteredSteps.slice(insertAt)],
  };
};

const useConfigHelpers = (config: CardCustomizerConfig) => {
  const isStepEnabled = useCallback(
    (step: NavigationStep) => {
      return config.steps.includes(step);
    },
    [config],
  );

  return useMemo(
    () => ({
      isStepEnabled,
    }),
    [isStepEnabled],
  );
};

export const useCardCustomizerConfig = (format: StationeryDraftFormat, category: StationeryTemplateCategoryEnum, dimensions?: CardDimensions, productType?: string) => {
  const [isPostcardRecipientsEnabled, isPostcardRecipientsLoading] = useIsFeatureEnabled('printPostcardRecipientsEnabled');
  // Depend on the primitive values so inline `{ width, height }` objects at call
  // sites don't defeat the memo.
  const width = dimensions?.width;
  const height = dimensions?.height;
  const config = useMemo(
    () => resolveCardCustomizerConfig(format, category, width === undefined || height === undefined ? undefined : { width, height }, isPostcardRecipientsEnabled, productType),
    [format, category, width, height, isPostcardRecipientsEnabled, productType],
  );
  const helpers = useConfigHelpers(config);

  return {
    config,
    helpers,
    // False while the remote postcardRecipients flag is still resolving —
    // consumers that act on the step list synchronously (e.g. the initial
    // ?step= URL guard) must wait, or a reload on ?step=postcardRecipients
    // gets bounced to cardFront by the flag's temporary default.
    isReady: !isPostcardRecipientsLoading,
  };
};
